/**
 * Single source of truth for checkout (Saleor channel) and reference FX currencies.
 * Used by GEO modal, footer, pricing/FX, and future currency pickers.
 */

export const SALEOR_CHANNEL_SLUGS = ["eu", "us"] as const;
export type SaleorChannelSlug = (typeof SALEOR_CHANNEL_SLUGS)[number];

/** Canonical settlement currency per storefront channel (matches Saleor). */
export const CHECKOUT_CURRENCY_BY_CHANNEL: Record<SaleorChannelSlug, string> = {
	eu: "EUR",
	us: "USD",
};

/**
 * All ISO 4217 codes we request from Frankfurter for reference pricing.
 * Order: global majors, then EU/EEA non-euro, then ECB extras + EUR.
 * Keep in sync with `src/lib/pricing/fx-rates.ts` consumer.
 */
export const REFERENCE_FX_CURRENCY_CODES = [
	"AUD",
	"BRL",
	"CAD",
	"CHF",
	"GBP",
	"ILS",
	"INR",
	"KRW",
	"MXN",
	"MYR",
	"NOK",
	"NZD",
	"PHP",
	"SGD",
	"THB",
	"TRY",
	"USD",
	"ZAR",
	"CZK",
	"DKK",
	"HUF",
	"ISK",
	"PLN",
	"RON",
	"SEK",
	"IDR",
	"EUR",
] as const;

/**
 * Code-point comparator for ISO 4217 codes — deliberately NOT `localeCompare`.
 *
 * `localeCompare` resolves against the runtime's default locale: the Node process on the server,
 * the browser's language on the client. Czech collation treats "ch" as one letter sorted after
 * "h", so `CHF` moves behind `HUF` in a `cs-CZ` browser. The currency list ships inside SSR
 * markup (footer), so a runtime-dependent order desyncs hydration (React error #418).
 * Currency codes carry no language, so there is nothing to gain from locale-aware collation.
 */
export function compareCurrencyCodes(a: string, b: string): number {
	if (a < b) return -1;
	if (a > b) return 1;
	return 0;
}

/** `REFERENCE_FX_CURRENCY_CODES` in a stable order, identical on server and client. */
export const REFERENCE_FX_CURRENCY_CODES_SORTED: readonly string[] = [...REFERENCE_FX_CURRENCY_CODES].sort(
	compareCurrencyCodes,
);

/**
 * Settlement currency for the current URL channel (`eu` → EUR, `us` → USD).
 */
export function getCheckoutCurrencyCode(channelSlug: string): "EUR" | "USD" {
	const s = channelSlug.toLowerCase();
	if (s.includes("us")) return "USD";
	return "EUR";
}
